import { Text } from "@/components/ui/baseUi/text"
import { useSessionSidebar } from "@/modules/sessions/sessionForm/hooks/useSessionSidebar"
import {
  TrainingBoxSlotEntity,
  TrainingProgramInfiniteSessionEntity,
  TrainingProgramOnOffSessionEntity,
} from "@/modules/training/entities/trainingContentItemEntity"
import { MarkerIcon } from "@images/svgIcons/markerIcon"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { ProgramType } from "@modules/programs/types/programTypes"
import dayjs from "dayjs"
import { useMemo } from "react"
import styled from "styled-components"

type SessionCardProps = {
  session: TrainingProgramInfiniteSessionEntity | TrainingProgramOnOffSessionEntity | TrainingBoxSlotEntity
}

export const SessionCard: React.FC<SessionCardProps> = ({ session }) => {
  const mySpaceNavigate = useMySpaceNavigate()
  const { navigateToSessionView } = useSessionSidebar()

  const sessionName = useMemo(() => {
    if (session instanceof TrainingBoxSlotEntity) {
      return session.program.name
    } else {
      return session.name
    }
  }, [session])

  const progOrBoxName = useMemo(() => {
    if (session instanceof TrainingBoxSlotEntity) {
      return session.box.name
    } else {
      return session.programName
    }
  }, [session])

  const onSelectedSession = (
    session: TrainingProgramInfiniteSessionEntity | TrainingProgramOnOffSessionEntity | TrainingBoxSlotEntity,
  ) => {
    if (session instanceof TrainingProgramInfiniteSessionEntity) {
      mySpaceNavigate(Path.MySpace.Training)
      setTimeout(() => void navigateToSessionView({ ...session, programType: ProgramType.VIRTUAL }), 100)
    } else if (session instanceof TrainingProgramOnOffSessionEntity) {
      mySpaceNavigate(Path.MySpace.Training)
      setTimeout(() => void navigateToSessionView({ ...session, programType: ProgramType.ONOFF }), 100)
    } else if (session instanceof TrainingBoxSlotEntity) {
      // TO DO
    }
  }

  return (
    <div
      className="mt-2 flex w-full cursor-pointer flex-col justify-between rounded-4xl bg-linear-95 from-green-800 to-green-600 p-6 transition-discrete duration-150 hover:scale-101"
      onClick={() => onSelectedSession(session)}
    >
      <div className="flex w-full justify-between">
        <Text fontStyle="TM" className="w-full wrap-break-word text-textOnColor-body">
          {sessionName}
        </Text>
        {session instanceof TrainingBoxSlotEntity && (
          <Text fontStyle="BM" className="text-textOnColor-body">
            {dayjs(session.startDate).format("H:mm")}
          </Text>
        )}
      </div>
      <div className="mt-1 flex items-center">
        {session instanceof TrainingBoxSlotEntity && (
          <MarkerIcon color="currentColor" className="mr-1 text-primary-500" width={15} height={15} />
        )}
        <Text fontStyle="BM" className="wrap-anywhere text-secondary-200">
          {progOrBoxName}
        </Text>
      </div>
      <div className="mt-4 flex flex-initial items-center justify-between rounded-3xl">
        <div className="flex items-center">
          <ProfileAvatar
            className="mr-2 size-10"
            profile={{ avatar: session.owner?.avatar ?? null, username: session.owner?.username ?? "" }}
          />
          <div className="flex flex-col">
            <Text fontStyle="LL" className="break-all text-white">
              {`${session.owner?.firstname ?? ""} ${session.owner?.lastname ?? ""}`}
            </Text>
            <Text fontStyle="BS" className="text-secondary-200">
              {word("program.label.author")}
            </Text>
          </div>
        </div>
        <div>
          {session instanceof TrainingBoxSlotEntity && (
            <div className="relative">
              <Subscribers fontStyle="LM">
                {`${session.registeredParticipantCount}/${session.participantLimit} ${word("slot.participants", { count: session.registeredParticipantCount })}`}
              </Subscribers>
              <Going fontStyle="LM">{word("programs.going")}</Going>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// TODO : remove styled components for tailwind
const Subscribers = styled(Text)`
  background-color: var(--secondary-800);
  color: var(--secondary-200);
  padding: 10px 14px;
  border-radius: 18px;
`
const Going = styled(Text)`
  background-color: var(--secondary-500);
  padding: 3px 8px;
  border-radius: 10px;
  color: var(--textOnColor-body);
  transform: rotate(-10deg);
  position: absolute;
  left: 9px;
  bottom: 24px;
`
